//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//? Import PaginationData type
import type { Pagination } from "../../services/stores/types";

//? Import CSVTeacherValidator type
import { CSVTeacherValidator } from "../../utils/csv/csv-validatorTeacher";

//? Import types from types
import type {
  Teacher,
  TeacherImportResult,
  TeachersDetails,
  TeacherResponse,
} from "../teachers/types/types";

// API methods for Teacher operations
export const teacherAPI = {
  // Fetch all Teachers with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<TeacherResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/teachers?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          teachers: data.teachers || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching teachers:", error);
        throw new Error("Failed to fetch teachers");
      });
  },

  // Fetch a Teacher by ID
  getById: async function (
    id: string,
    token: string
  ): Promise<TeachersDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/teachers/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching Teacher:", error);
        throw new Error("Failed to fetch Teacher");
      });
  },

  // Create a new Teacher
  create: async function (
    data: { name: string; email: string },
    token: string
  ): Promise<Teacher> {
    try {
      const response = await api.request({
        url: `${API_URI}/admin/private/teachers`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error creating admin:", error, data);
      throw error;
    }
  },

  createBatch: async function (
    teachers: CSVTeacherValidator[],
    token: string
  ): Promise<TeacherImportResult> {
    return api
      .request({
        url: `${API_URI}/admin/private/teachers/batch`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { teachers },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error("Error processing CSVTeacher:", error);
        throw error;
      });
  },

  // Update an existing Teacher
  update: async function (
    id: string,
    data: {
      name: string;
      email: string;
      reset_password?: boolean;
      is_auditor?: boolean;
      allow_select_all_questions?: boolean;
    },
    token: string
  ): Promise<Teacher> {
    return api
      .request({
        url: `${API_URI}/admin/private/teachers/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating Teacher:", error);
        throw new Error("Failed to update Teacher");
      });
  },

  // Delete an Teacher by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/teachers/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting Teacher:", error);
        throw new Error("Failed to delete Teacher");
      });
  },

  // Restore an Teacher by ID
  restoreByBatch: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/teachers/restore/batch/${id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Teacher:", error);
        throw new Error("Failed to restore Teacher");
      });
  },

  restoreByEmail: async function (email: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/teachers/restore/email/`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { email },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Teacher:", error);
        throw new Error("Failed to restore Teacher");
      });
  },
};
